import { CaseType } from '@/constants';
import { CasePortability } from '@/interfaces';
import type { SelectOption } from 'naive-ui';

/**
 * Portabilidade de garantia locatícia. Espelha `CasePortabilityRules.IsGuaranteePortability`.
 *
 * Duas pegadinhas: `CasePortability.PortoSeguro === 0` (então `!!portability` seria falsy
 * para a Porto), e `is.portability` é sobrecarregado — todo caso de Seguro Incêndio nasce
 * com PortoSeguro significando "emitido via Porto", não origem de portabilidade de fiança.
 */
export function isGuaranteePortability(
	portability: CasePortability | null | undefined,
	caseType: CaseType | number | null | undefined,
): boolean {
	return portability != null && caseType !== CaseType.CASE_FIRE_INSURANCE;
}

export interface PortabilityOperationDisplay {
	origin: string;
	startedAt: string;
	renewalAt: string;
}

/** Campos de portabilidade para "Informações da operação" (origem, início, próxima renovação). */
export function getPortabilityOperationDisplay(params: {
	portability: CasePortability | null | undefined;
	caseType: CaseType | number | null | undefined;
	startedAt: string | Date | null | undefined;
	renewalAt?: string | Date | null;
}): PortabilityOperationDisplay | null {
	if (!isGuaranteePortability(params.portability, params.caseType)) return null;

	const origin = getCasePortabilityName(params.portability) || 'Não informado';
	const startedAt = formatDateOnlyBr(params.startedAt) || 'Não informado';
	const renewalSource =
		params.renewalAt ?? (params.startedAt ? nextTermEndOnOrAfter(params.startedAt) : null);
	const renewalAt = formatDateOnlyBr(renewalSource) || 'Não informado';

	return { origin, startedAt, renewalAt };
}

/**
 * Fonte única dos rótulos de seguradora de origem de portabilidade.
 *
 * Consumido por:
 *  - CasePortabilityTag (exibe a seguradora no caso)
 *  - PortabilityStep (select da seguradora de origem na criação da proposta)
 *
 * Manter alinhado com o enum CasePortability (src/interfaces/IProposal.ts).
 * Todo membro do enum DEVE ter um rótulo aqui — um faltante volta a ser o bug
 * do Credipago (tag em branco).
 */
export function getCasePortabilityName(portability: CasePortability | null | undefined): string {
	switch (portability) {
		case CasePortability.PortoSeguro:
			return 'Porto Seguro';
		case CasePortability.QuintoCred:
			return 'Quinto Cred';
		case CasePortability.Credipago:
			return 'Credipago';
		case CasePortability.Loft:
			return 'Loft';
		case CasePortability.Avalyst:
			return 'Avalyst';
		case CasePortability.Imovpago:
			return 'Imovpago';
		case CasePortability.FiancaRapida:
			return 'Fiança Rápida';
		case CasePortability.Fiantec:
			return 'Fiantec';
		case CasePortability.SeuFiador:
			return 'Seu Fiador';
		case CasePortability.Pottencial:
			return 'Pottencial';
		case CasePortability.Garantti:
			return 'Garantti';
		case CasePortability.TioGiba:
			return 'Tio Giba';
		default:
			return '';
	}
}

/**
 * Opções para o NSelect da seguradora de origem. Derivado dos membros numéricos
 * do enum para nunca sair de sincronia com CasePortability.
 */
export const casePortabilityOptions: SelectOption[] = Object.values(CasePortability)
	.filter((value): value is CasePortability => typeof value === 'number')
	.map((value) => ({
		label: getCasePortabilityName(value),
		value,
	}));

/** Início na garantidora: idade ≥ 365 dias bloqueia (limite inclusive). */
export const PORTABILITY_STARTED_AT_MAX_AGE_DAYS = 365;

/** Início da locação: idade ≥ 18 meses de calendário bloqueia (limite inclusive). */
export const PORTABILITY_LEASE_STARTED_AT_MAX_AGE_MONTHS = 18;

/** Parse date-only (YYYY-MM-DD) em horário local — evita shift UTC de `new Date('YYYY-MM-DD')`. */
export function parseDateOnly(value: string | null | undefined): Date | null {
	if (!value) return null;
	const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
	if (!match) return null;
	const year = Number(match[1]);
	const month = Number(match[2]);
	const day = Number(match[3]);
	const date = new Date(year, month - 1, day);
	if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
		return null;
	}
	return date;
}

export function formatDateOnlyBr(value: string | Date | null | undefined): string {
	const date = value instanceof Date ? value : parseDateOnly(value);
	if (!date) return '';
	return date.toLocaleDateString('pt-BR');
}

/**
 * Espelha `DateHelperBrt.NextTermEndOnOrAfter`: startedAt+365 dias, avança em blocos de 365
 * até ≥ hoje. Preview do front alinhado ao RenewalAt da API.
 */
export function nextTermEndOnOrAfter(
	startedAt: string | Date | null | undefined,
	asOf: Date = new Date(),
): Date | null {
	const start = startedAt instanceof Date ? startedAt : parseDateOnly(startedAt);
	if (!start) return null;

	const today = new Date(asOf.getFullYear(), asOf.getMonth(), asOf.getDate());
	const renewal = new Date(start.getFullYear(), start.getMonth(), start.getDate());
	renewal.setDate(renewal.getDate() + 365);
	while (renewal < today) {
		renewal.setDate(renewal.getDate() + 365);
	}
	return renewal;
}

/**
 * Faixa da data de início na garantidora atual:
 * - obrigatória e válida
 * - não futura
 * - idade ≥ {@link PORTABILITY_STARTED_AT_MAX_AGE_DAYS} dias bloqueia
 */
export function validatePortabilityStartedAt(
	value: string | null | undefined,
	asOf: Date = new Date(),
): string | null {
	if (!value) return 'Data de início na garantidora é obrigatória';

	const start = parseDateOnly(value);
	if (!start) return 'Data de início inválida';

	const today = new Date(asOf.getFullYear(), asOf.getMonth(), asOf.getDate());
	if (start > today) return 'A data de início não pode ser futura';

	const oldest = new Date(today);
	oldest.setDate(oldest.getDate() - PORTABILITY_STARTED_AT_MAX_AGE_DAYS);
	// Limite inclusive: exatamente 365 dias atrás também bloqueia.
	if (start <= oldest) {
		return 'A data de início não pode ter 1 ano ou mais';
	}

	return null;
}

/** Date local → YYYY-MM-DD (o formato date-only que o AppInputDate emite e a API recebe). */
function toDateOnlyIso(date: Date): string {
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${date.getFullYear()}-${month}-${day}`;
}

function shiftDateOnly(value: string | null | undefined, days: number): string | null {
	const date = parseDateOnly(value);
	if (!date) return null;
	date.setDate(date.getDate() + days);
	return toDateOnlyIso(date);
}

/**
 * O parceiro informa a renovação da garantia atual; a API recebe o início do período vigente
 * (`startedAt`) e recalcula `RenewalAt = startedAt + 365` — que cai exatamente na data digitada.
 * Assim a regra de 1 ano do backend vale sem mudança.
 */
export function portabilityStartedAtFromRenewal(
	renewalAt: string | null | undefined,
): string | null {
	return shiftDateOnly(renewalAt, -PORTABILITY_STARTED_AT_MAX_AGE_DAYS);
}

/** Inverso de {@link portabilityStartedAtFromRenewal}, para exibir o campo a partir do estado. */
export function portabilityRenewalFromStartedAt(
	startedAt: string | null | undefined,
): string | null {
	return shiftDateOnly(startedAt, PORTABILITY_STARTED_AT_MAX_AGE_DAYS);
}

/**
 * Renovação da garantia atual — a mesma faixa de {@link validatePortabilityStartedAt}, vista
 * pela renovação (início + 365):
 * - obrigatória e válida
 * - futura (início há menos de 1 ano)
 * - no máximo 365 dias à frente (início não futuro)
 */
export function validatePortabilityRenewalAt(
	value: string | null | undefined,
	asOf: Date = new Date(),
): string | null {
	if (!value) return 'Data de renovação da garantia atual é obrigatória';

	const renewal = parseDateOnly(value);
	if (!renewal) return 'Data de renovação inválida';

	const today = new Date(asOf.getFullYear(), asOf.getMonth(), asOf.getDate());
	if (renewal <= today) return 'A data de renovação precisa ser futura';

	const latest = new Date(today);
	latest.setDate(latest.getDate() + PORTABILITY_STARTED_AT_MAX_AGE_DAYS);
	if (renewal > latest) return 'A data de renovação não pode passar de 1 ano à frente';

	return null;
}

/**
 * Início da locação (assinatura do contrato de locação):
 * - obrigatória e válida
 * - não futura
 * - idade ≥ {@link PORTABILITY_LEASE_STARTED_AT_MAX_AGE_MONTHS} meses bloqueia
 */
export function validatePortabilityLeaseStartedAt(
	value: string | null | undefined,
	asOf: Date = new Date(),
): string | null {
	if (!value) return 'Data de início da locação é obrigatória';

	const start = parseDateOnly(value);
	if (!start) return 'Data de início da locação inválida';

	const today = new Date(asOf.getFullYear(), asOf.getMonth(), asOf.getDate());
	if (start > today) return 'A data de início da locação não pode ser futura';

	const oldest = new Date(today);
	oldest.setMonth(oldest.getMonth() - PORTABILITY_LEASE_STARTED_AT_MAX_AGE_MONTHS);
	if (start <= oldest) {
		return 'A data de início da locação não pode ter 18 meses ou mais';
	}

	return null;
}
